// @ts-strict-ignore
import {Component, ContentChildren, Directive, effect, input, output, signal, TemplateRef} from "@angular/core";
import {
	collapseOnLeaveAnimation,
	fadeOutCollapseOnLeaveAnimation,
	fadeOutUpOnLeaveAnimation,
	zoomInOnEnterAnimation,
	zoomOutOnLeaveAnimation,
} from "angular-animations";
import {SimUtilityService} from "@services";
import {PanelBase} from "../panel-base/panel-base";
import {ControlPanel, ControlPanelType, PanelWarning} from "@models";
import {Translations} from "@translations";
import {MatIconButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {CdkDrag, CdkDragHandle, CdkDropList} from "@angular/cdk/drag-drop";
import {MatRipple} from "@angular/material/core";
import {MatTooltip} from "@angular/material/tooltip";
import {MatIcon} from "@angular/material/icon";
import {MatCheckbox} from "@angular/material/checkbox";
import {StopPropagationDirective} from "@common/pipes/stop-propagation.directive";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {NgTemplateOutlet} from "@angular/common";
import {MatDivider} from "@angular/material/divider";
import {AnimatedMaterialIconComponent} from "@common/ui/animated-material-icon/animated-material-icon.component";
import {explicitEffect} from "ngxtension/explicit-effect";
import {EnumLabel, EnumLabelPipe} from "../../../../shared/shared-pipes/enum-label.pipe";

@Directive({
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: "[marker]",
})
export class Marker {}

@Directive({
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: "[header]",
})
export class Header {}

@Directive({
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: "[matMenuAdditionalOptions]",
})
export class MatMenuAdditionalOptions {}

@Component({
	selector: "sim-panel",
	templateUrl: "./panel.component.html",
	styleUrls: ["./panel.component.scss"],
	animations: [
		zoomInOnEnterAnimation({duration: 250}),
		zoomOutOnLeaveAnimation({duration: 250}),
		fadeOutUpOnLeaveAnimation({duration: 175}),
		fadeOutCollapseOnLeaveAnimation({duration: 1075}),
		collapseOnLeaveAnimation({duration: 175}),
	],
	imports: [
		MatRipple,
		MatIconButton,
		MatTooltip,
		TranslateModule,
		CdkDropList,
		CdkDrag,
		MatIcon,
		MatCheckbox,
		StopPropagationDirective,
		MatMenu,
		MatMenuItem,
		NgTemplateOutlet,
		MatMenuTrigger,
		MatDivider,
		CdkDragHandle,
		AnimatedMaterialIconComponent,
		EnumLabelPipe,
	],
})
export class PanelComponent extends PanelBase {
	T = Translations;
	readonly ControlPanelType = ControlPanelType;
	readonly EnumLabel = EnumLabel;

	// I/O
	public readonly showEditMenu = input<boolean>(true);
	public readonly showUndockedPanels = input<boolean>(false);
	public readonly showSelectAll = input<boolean>(false);
	public readonly showControls = input<boolean>(true);
	public readonly warning = input<PanelWarning | null>(null);
	public readonly selectAllClicked = output<void>();

	// Properties
	disableRipple = signal<boolean>(false);
	maximizedPanel = signal<ControlPanel | null>(null);

	@ContentChildren(Marker, {read: TemplateRef}) templates: any;
	@ContentChildren(Header, {read: TemplateRef}) templatesHeader: any;
	@ContentChildren(MatMenuAdditionalOptions, {read: TemplateRef})
	templatesMatMenuAdditionalOptions: any;

	readonly #closeAllOtherPanels = effect(() => {
		if (this.showUndockedPanels()) {
			this.closeAllOtherPanels(this.panelOptions().activePanel);
		}
	});

	readonly #maximizePanel = explicitEffect([this.maximizedPanel], () => {
		const maximizedPanel = this.maximizedPanel();
		if (!maximizedPanel) {
			return;
		}
		maximizedPanel.isOpen = true;
		this.panelOptions().activePanel = maximizedPanel;
		this.closeAllOtherPanels(maximizedPanel);
		this.updatePanelOpenState(maximizedPanel);
	});

	public async togglePanel(panel: ControlPanel) {
		if (!this.openable()) {
			return;
		}
		//close all panels except active one if it should be toggled
		const activePanel = this.panelOptions().activePanel?.id === panel.id ? this.panelOptions().activePanel : null;
		this.closeAllOtherPanels(activePanel);
		await SimUtilityService.delay(250);

		panel.isOpen = !panel.isOpen;
		this.updatePanelOpenState(panel);

		this.panelOptions().activePanel = panel.isOpen ? panel : null;
	}

	protected iconClicked() {
		if (this.arrowBackActive()) {
			this.arrowBackClicked.emit();
		}
	}

	private closeAllOtherPanels(activePanel: ControlPanel) {
		for (const panel of this.panels()) {
			if (panel.id !== activePanel?.id) {
				if (panel.isOpen && (!panel.undocked || this.showUndockedPanels())) {
					panel.isOpen = false;
					this.updatePanelOpenState(panel);
				}
			}
		}
	}
}
